import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  MessageSquare, 
  TrendingUp, 
  Globe, 
  Cpu, 
  LogOut, 
  Menu, 
  X,
  Shield,
  Zap,
  Crown,
  Settings,
  Command
} from 'lucide-react';

// Import modules
import VisualShockModule from '../modules/VisualShockModule';
import ObjectionCrusherModule from '../modules/ObjectionCrusherModule';
import JardinSecretModule from '../modules/JardinSecretModule';
import EcosystemModule from '../modules/EcosystemModule';
import OverviewModule from '../modules/OverviewModule';
import CommandCenterModule from '../modules/CommandCenterModule';
import AssistantPage from './AssistantPage';
import SanctuaryModule from '../modules/SanctuaryModule';
import SettingsModule from '../modules/SettingsModule';
import AgentsModule from '../modules/AgentsModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [secretEnabled, setSecretEnabled] = useState(false);
  const [sanctuaryOpen, setSanctuaryOpen] = useState(false);
  const [masterSwitchClicks, setMasterSwitchClicks] = useState(0);
  const [systemStatus, setSystemStatus] = useState({ status: 'operational' });

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/health`);
        setSystemStatus(response.data);
      } catch (error) {
        setSystemStatus({ status: 'error' });
      }
    };
    fetchStatus();
  }, []);

  // Master Switch activation (5 clicks to open Sanctuary)
  const handleMasterSwitch = () => {
    setSecretEnabled(true);
    setSanctuaryOpen(true);
    toast.success('SANCTUARY ACTIVATED', {
      style: {
        background: '#000',
        border: '1px solid #FF0000',
        color: '#FF0000'
      }
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'OVERVIEW', icon: LayoutDashboard },
    { path: '/command-center', label: 'COMMAND CENTER', icon: Command },
    { path: '/assistant', label: 'ASSISTANT', icon: MessageSquare },
    { path: '/visual-shock', label: 'VISUAL SHOCK', icon: TrendingUp },
    { path: '/objection-crusher', label: 'OBJECTION CRUSHER', icon: MessageSquare },
    { path: '/ecosystem', label: 'ECOSYSTEM', icon: Cpu },
  { path: '/agents', label: 'AGENTS', icon: Cpu },
  { path: '/settings', label: 'SETTINGS', icon: Settings },
  ];

  if (secretEnabled && ['admin', 'founder'].includes(user?.role)) {
    navItems.push({ path: '/jardin-secret', label: 'JARDIN SECRET', icon: Globe, secret: true });
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-[#0A0A0A] border border-white/10 text-[#EDEDED]"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0A0A0A] border-r border-white/10
        transform transition-transform duration-300 z-40
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-[#DC143C]" />
            <h1 className="font-heading text-xl font-bold tracking-wider text-[#EDEDED]">
              SELENA
            </h1>
          </div>
          <p className="font-mono text-[10px] text-[#888888] tracking-widest">
            MATRIX COMMANDER v1.0
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6">
          {navItems.map((item) => (
            <div
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              className={`nav-item ${isActive(item.path) ? 'active' : ''} ${item.secret ? 'text-[#00FF9D]' : ''}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
              {item.secret && <Shield className="w-3 h-3 ml-auto" />}
            </div>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-heading text-sm text-[#EDEDED] uppercase">{user?.username}</p>
              <p className="font-mono text-[10px] text-[#DC143C]">{user?.role?.toUpperCase()}</p>
            </div>
            <div className={`w-2 h-2 ${systemStatus.status === 'operational' ? 'status-online' : 'status-offline'}`} />
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full btn-secondary py-2 text-xs flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            DISCONNECT
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur border-b border-white/10">
          <div className="px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4 lg:ml-0 ml-12">
              <h2 className="font-heading text-lg font-bold text-[#EDEDED] uppercase tracking-wider">
                {navItems.find(item => isActive(item.path))?.label || 'COMMAND CENTER'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-xs text-[#888888]">
                {new Date().toLocaleDateString('fr-FR', { 
                  day: '2-digit', 
                  month: 'short', 
                  year: 'numeric' 
                }).toUpperCase()}
              </span>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 ${systemStatus.status === 'operational' ? 'status-online' : 'status-offline'}`} />
                <span className="font-mono text-xs text-[#888888]">
                  {systemStatus.status?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Routes */}
        <div className="p-6 lg:p-8">
          <Routes>
            <Route path="/command-center" element={<CommandCenterModule token={token} />} />
            <Route path="/assistant" element={<AssistantPage />} />
                <Route path="/" element={<OverviewModule token={token} />} />
            <Route path="/visual-shock" element={<VisualShockModule token={token} />} />
            <Route path="/objection-crusher" element={<ObjectionCrusherModule token={token} />} />
            <Route path="/ecosystem" element={<EcosystemModule token={token} />} />
            <Route path="/agents" element={<AgentsModule token={token} />} />
            <Route path="/settings" element={<SettingsModule token={token} />} />
            {secretEnabled && ['admin', 'founder'].includes(user?.role) && (
              <Route path="/jardin-secret" element={<JardinSecretModule token={token} />} />
            )}
          </Routes>
        </div>
      </main>

      {/* Master Switch (Hidden trigger) */}
      <div
        data-testid="master-switch"
        onClick={handleMasterSwitch}
        className="master-switch"
        title={secretEnabled ? 'SANCTUARY READY' : ''}
      >
        {secretEnabled ? (
          <Crown className="w-4 h-4 text-[#FF0000] animate-pulse" />
        ) : (
          <Shield className="w-4 h-4 text-[#444444]" />
        )}
      </div>

      {/* Sanctuary Full-Screen Modal */}
      <SanctuaryModule 
        isOpen={sanctuaryOpen}
        onClose={() => setSanctuaryOpen(false)}
        token={token}
      />

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

